import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '@/lib/api';
import { setTokens, clearTokens, getAccessToken } from '@/lib/auth-storage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    const { data } = await api.get('/auth/me');
    setUser(data.user);
    setCompany(data.company);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const token = getAccessToken();
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        await refreshMe();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to restore session:', err?.message || err);
        clearTokens();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [refreshMe]);

  const login = useCallback(async (email, password, slug = null) => {
    const payload = { email, password };
    if (slug) {
      payload.slug = slug;
    }
    const { data } = await api.post('/auth/login', payload);
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setUser(data.user);
    setCompany(data.company);
    return data;
  }, []);

  const logout = useCallback(async () => {
    // Salvar slug da empresa antes de limpar sessão
    const userCompanySlug = user?.company?.layout?.slug;
    
    try { await api.post('/auth/logout'); } catch (err) {
      // eslint-disable-next-line no-console
      console.debug('Logout API call failed (continuing):', err?.message || err);
    }
    clearTokens();
    setUser(null);
    setCompany(null);
    
    // Redirecionar para login da empresa ou login Super Admin
    const loginPath = userCompanySlug ? `/${userCompanySlug}/login` : '/login';
    window.location.href = loginPath;
  }, [user]);

  const hasRole = useCallback((...roles) => {
    if (!user) return false;
    return roles.some((r) => (user.roles || []).some(role => role.type === r));
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, company, loading, login, logout, refreshMe, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
